import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowUpRight } from "lucide-react";
import { useLanguage } from "./LanguageContext";
import { Reveal } from "./Reveal";
import { WEDDING, IMAGES } from "./config";
import { CornerOrnament, IznikTexture } from "./Motifs";
import SaveActions from "./SaveActions";

export default function LocationSection() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["-14%", "14%"]);

  return (
    <section
      ref={ref}
      data-testid="location-section"
      className="relative bg-cream py-28 md:py-40"
    >
      <IznikTexture size={320} />
      <div className="relative z-10 mx-auto max-w-6xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* text */}
        <div className="lg:col-span-5">
          <Reveal>
            <span className="font-sans text-xs uppercase tracking-[0.28em] text-pinky">
              04 — {t.location.kicker}
            </span>
            <h2 className="mt-5 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink">
              {t.location.title}
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 flex items-start gap-3 text-ink/80">
              <MapPin className="mt-1 h-5 w-5 shrink-0 text-turq" strokeWidth={1.6} />
              <div>
                <p className="font-sans text-base md:text-lg">{t.location.address}</p>
                <p className="mt-2 font-sans text-sm text-ink/55">{t.location.when}</p>
              </div>
            </div>
            <p className="mt-6 max-w-md font-sans text-base leading-relaxed text-ink/70">
              {t.location.desc}
            </p>

            <a
              href={WEDDING.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="maps-button"
              className="group mt-9 inline-flex items-center gap-3 rounded-full bg-ink px-7 py-3.5 text-cream transition-colors duration-300 hover:bg-turq"
            >
              <span className="font-sans text-sm uppercase tracking-[0.14em]">
                {t.location.button}
              </span>
              <ArrowUpRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={1.8}
              />
            </a>

            <SaveActions />
          </Reveal>
        </div>

        {/* image */}
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="relative aspect-[4/3] overflow-hidden border border-gold/40">
              <CornerOrnament className="absolute left-2 top-2 z-10 w-10" />
              <CornerOrnament className="absolute bottom-2 right-2 z-10 w-10 rotate-180" />
              <motion.img
                style={{ y: yImg }}
                src={IMAGES.park}
                alt="Park de Heerlijkheid"
                className="absolute inset-0 h-[128%] w-full object-cover -top-[14%]"
                loading="lazy"
              />
              <div className="absolute left-4 top-4 rounded-full bg-cream/85 backdrop-blur px-4 py-1.5">
                <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-ink">
                  Rotterdam · NL
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Park de Heerlijkheid gallery */}
      <div className="relative z-10 mx-auto mt-16 md:mt-24 max-w-6xl px-6 md:px-10">
        <Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {IMAGES.parkGallery.map((src, i) => (
              <div
                key={i}
                data-testid={`park-photo-${i}`}
                className={`group relative overflow-hidden border border-gold/40 ${
                  i === 0 ? "col-span-2 md:col-span-1 aspect-[4/3]" : "aspect-[4/3]"
                }`}
              >
                <img
                  src={src}
                  alt={`Park de Heerlijkheid ${i + 1}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/10" />
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
